import { clearTrackingCookies, readConsent } from './trackingConsent';

export const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-H15ZL38ZL2';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
    [key: `ga-disable-${string}`]: boolean | undefined;
  }
}

const CAMPAIGN_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'];

export const analyticsPage = () => {
  const url = new URL(window.location.href);
  const query = new URLSearchParams();
  for (const key of CAMPAIGN_KEYS) {
    const value = url.searchParams.get(key);
    if (value && /^[a-zA-Z0-9_-]{1,150}$/.test(value)) query.set(key, value);
  }
  let referrer = '';
  try {
    const source = new URL(document.referrer);
    if (['http:', 'https:'].includes(source.protocol)) referrer = source.origin;
  } catch { /* Direct visits have no referrer. */ }
  return {
    page_location: `${url.origin}${url.pathname}${query.toString() ? `?${query}` : ''}`,
    page_path: url.pathname,
    page_referrer: referrer,
  };
};

export const syncGoogleAnalytics = () => {
  const granted = readConsent()?.analytics === true && /^G-[A-Z0-9]+$/.test(GA_MEASUREMENT_ID);
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = !granted;
  if (!granted) {
    window.gtag?.('consent', 'update', { analytics_storage: 'denied' });
    clearTrackingCookies(['_ga', '_gid', '_gat']);
    return false;
  }
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    // Google consumes Arguments objects, not ordinary dataLayer event arrays.
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag('consent', 'default', {
      analytics_storage: 'denied', ad_storage: 'denied',
      ad_user_data: 'denied', ad_personalization: 'denied',
    });
  }
  window.gtag('consent', 'update', { analytics_storage: 'granted' });
  window.gtag('set', analyticsPage());
  if (!document.querySelector('script[data-certy-analytics]')) {
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      ...analyticsPage(),
    });
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.dataset.certyAnalytics = GA_MEASUREMENT_ID;
    script.referrerPolicy = 'origin';
    document.head.appendChild(script);
  }
  return true;
};

export const trackGoogleEvent = (name: string, params: Record<string, unknown> = {}) => {
  if (!syncGoogleAnalytics()) return false;
  window.gtag?.('event', name, { ...params, ...analyticsPage(), send_to: GA_MEASUREMENT_ID });
  return true;
};
